import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmailDraftModal } from '../components/EmailDraftModal';
import CampaignUploadModal from '../components/CampaignUploadModal';

import { apiClient } from '../lib/apiClient';
import { CustomerProfile, CustomerStatus } from '../types/api';
import { 
  ArrowLeft,
  Edit,
  Trash2,
  AlertCircle,
  DollarSign,
  Activity,
  Target,
  TrendingUp,
  TrendingDown,
  X,
  ExternalLink,
  MessageSquare,
  Mail,
  Calendar,
  Users,
  CheckCircle,
  Upload,
  FileText,
} from 'lucide-react';
import TrelloIcon from '../components/icons/TrelloIcon';
import LinearIcon from '../components/icons/LinearIcon';
import JiraIcon from '../components/icons/JiraIcon';
import SlackIcon from '../components/icons/SlackIcon';

const CustomerDetailPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [showBriefingModal, setShowBriefingModal] = useState(false);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingData, setBriefingData] = useState<any>(null);
  const [showEmailDraftModal, setShowEmailDraftModal] = useState(false);
  const [emailDraftLoading, setEmailDraftLoading] = useState(false);
  const [emailDraftData, setEmailDraftData] = useState<any>(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);

  const fetchCustomerData = useCallback(async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      setError(null);
      const customerData = await apiClient.getCustomer(customerId);
      setCustomer(customerData as CustomerProfile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load customer data');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchCustomerData();
  }, [fetchCustomerData]);

  // Handle navigation state for briefing modal
  useEffect(() => {
    const state = location.state as { 
      briefing_data?: any; 
      show_briefing_modal?: boolean;
      email_draft_data?: any;
      show_email_draft_modal?: boolean;
    } | null;
    
    if (state?.show_briefing_modal && state?.briefing_data) {
      setBriefingData(state.briefing_data);
      setShowBriefingModal(true);
    }
    
    if (state?.show_email_draft_modal && state?.email_draft_data) {
      setEmailDraftData(state.email_draft_data);
      setShowEmailDraftModal(true);
    }
  }, [location.state]);

  const handleDeleteCustomer = async () => {
    if (!customerId) return;

    try {
      setDeleteLoading(true);
      await apiClient.deleteCustomer(customerId);
      navigate('/customers');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete customer');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleGenerateBriefing = async (sinceDate: string) => {
    if (!customerId) return;

    try {
      setBriefingLoading(true);
      const briefing = await apiClient.generateBriefing(customerId, sinceDate);
      setBriefingData(briefing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate briefing');
    } finally {
      setBriefingLoading(false);
    }
  };

  const handleDraftEmail = async (emailType: string = 'update') => {
    if (!customerId) return;

    try {
      setEmailDraftLoading(true);
      const emailDraft = await apiClient.post('/api/insights/draft-email', {
        customer_id: customerId,
        email_type: emailType,
      });
      setEmailDraftData(emailDraft.data);
      setShowEmailDraftModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to draft email');
    } finally {
      setEmailDraftLoading(false);
    }
  };

  const getHealthScoreColor = (score?: number) => {
    if (!score) return 'text-gray-400';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthScoreIcon = (score?: number) => {
    if (!score) return <AlertCircle className="w-5 h-5" />;
    if (score >= 80) return <TrendingUp className="w-5 h-5 text-green-600" />;
    return <TrendingDown className="w-5 h-5 text-red-600" />;
  };

  const getStatusVariant = (status: CustomerStatus): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'active': return 'default';
      case 'prospect': return 'secondary';
      case 'churned': return 'destructive';
      default: return 'outline';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/customers')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Accounts
          </Button>
        </div>
        
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center text-red-800">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error || 'Customer not found'}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/customers')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Accounts
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{customer.name}</h1>
            <p className="text-muted-foreground">Account Details & Analytics</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowBriefingModal(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Generate Briefing
          </Button>
          <Button 
            onClick={() => handleDraftEmail('update')}
            disabled={emailDraftLoading}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Mail className="w-4 h-4 mr-2" />
            {emailDraftLoading ? 'Drafting...' : 'Draft Email'}
          </Button>
          <Button 
            onClick={() => setShowCampaignModal(true)}
            variant="outline"
          >
            <Upload className="w-4 h-4 mr-2" />
            Campaign CSV
          </Button>
          <Button 
            onClick={() => navigate(`/customers/${customerId}/qbrs`)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <FileText className="w-4 h-4 mr-2" />
            QBR Reports
          </Button>
          <Button variant="outline" onClick={() => setShowEditModal(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Customer Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Health Score</p>
                <p className={`text-2xl font-bold ${getHealthScoreColor(customer.health_score)}`}>
                  {customer.health_score ? `${customer.health_score}/100` : 'N/A'}
                </p>
              </div>
              {getHealthScoreIcon(customer.health_score)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(customer.mrr)}</p>
              </div>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setShowSourcesModal(true)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Data Sources</p>
                <p className="text-2xl font-bold">{customer.sources_count}</p>
                <p className="text-xs text-gray-500 mt-1">Click to view details</p>
              </div>
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Status</p>
                <Badge variant={getStatusVariant(customer.status)} className="mt-2">
                  {customer.status}
                </Badge>
              </div>
              <Target className="w-5 h-5 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Company Name</label>
              <p className="text-lg font-semibold">{customer.name}</p>
            </div>
            
            {customer.domains.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-600">Email Domains</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {customer.domains.map((domain) => (
                    <Badge key={domain} variant="outline">{domain}</Badge>
                  ))}
                </div>
              </div>
            )}

            {customer.industry && (
              <div>
                <label className="text-sm font-medium text-gray-600">Industry</label>
                <p>{customer.industry}</p>
              </div>
            )}

            {customer.company_size && (
              <div>
                <label className="text-sm font-medium text-gray-600">Company Size</label>
                <p>{customer.company_size}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-600">Account Since</label>
              <p>{formatDate(customer.created_at)}</p>
            </div>
          </CardContent>
        </Card>

        {/* Contract Information */}
        <Card>
          <CardHeader>
            <CardTitle>Contract Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Monthly Recurring Revenue</label>
              <p className="text-lg font-semibold">{formatCurrency(customer.mrr)}</p>
            </div>

            {customer.contract_value && (
              <div>
                <label className="text-sm font-medium text-gray-600">Contract Value</label>
                <p className="text-lg font-semibold">{formatCurrency(customer.contract_value)}</p>
              </div>
            )}

            {customer.contract_start && (
              <div>
                <label className="text-sm font-medium text-gray-600">Contract Start</label>
                <p>{formatDate(customer.contract_start)}</p>
              </div>
            )}

            {customer.contract_end && (
              <div>
                <label className="text-sm font-medium text-gray-600">Contract End</label>
                <p>{formatDate(customer.contract_end)}</p>
              </div>
            )}

            {customer.crm_id && (
              <div>
                <label className="text-sm font-medium text-gray-600">CRM ID</label>
                <p className="font-mono text-sm">{customer.crm_id}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Customer Modal */}
      {showEditModal && customer && (
        <EditCustomerModal
          customer={customer}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchCustomerData(); // Refresh customer data
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmationModal
          customerName={customer?.name || ''}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteCustomer}
          loading={deleteLoading}
        />
      )}

      {/* Sources Modal */}
      {showSourcesModal && customer && (
        <SourcesModal
          customerId={customer.customer_id}
          customerName={customer.name}
          onClose={() => setShowSourcesModal(false)}
          onSourceChange={() => {
            // Refresh customer data when sources change
            fetchCustomerData();
          }}
        />
      )}

      {/* Briefing Modal */}
      {showBriefingModal && customer && (
        <BriefingModal
          isOpen={showBriefingModal}
          onClose={() => {
            setShowBriefingModal(false);
            setBriefingData(null);
          }}
          customer={customer}
          onGenerateBriefing={handleGenerateBriefing}
          loading={briefingLoading}
          briefingData={briefingData}
          onClearBriefing={() => setBriefingData(null)}
        />
      )}

      {/* Email Draft Modal */}
      <EmailDraftModal
        isOpen={showEmailDraftModal}
        onClose={() => {
          setShowEmailDraftModal(false);
          setEmailDraftData(null);
        }}
        emailData={emailDraftData}
      />

      {showCampaignModal && customer && (
        <CampaignUploadModal
          customerId={customer.customer_id}
          customerName={customer.name}
          onClose={() => setShowCampaignModal(false)}
        />
      )}
    </div>
  );
};

// Sources Modal Component
interface SourcesModalProps {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onSourceChange?: () => void;
}

const SourcesModal: React.FC<SourcesModalProps> = ({ customerId, customerName, onClose, onSourceChange }) => {
  const [sources, setSources] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingSource, setRemovingSource] = useState<string | null>(null);

  const fetchSources = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getCustomerSources(customerId) as { sources: any[] };
      setSources(response.sources || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  const handleRemoveSource = async (source: any) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove "${source.source_name}" from this customer? This will make it an unmapped source.`
    );
    
    if (!confirmed) return;

    try {
      setRemovingSource(source.source_id);
      await apiClient.unmapSourceFromCustomer(source.source_type, source.source_id);
      
      // Refresh sources list
      await fetchSources();
      
      // Notify parent component to refresh customer data
      onSourceChange?.();
    } catch (err) {
      console.error('Failed to remove source:', err);
      alert('Failed to remove source. Please try again.');
    } finally {
      setRemovingSource(null);
    }
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType.toLowerCase()) {
      case 'slack_channel':
        return (
          <div className="w-4 h-4 rounded bg-purple-100 flex items-center justify-center">
            <SlackIcon size={12} />
          </div>
        );
      case 'email_domain':
        return <Mail className="w-4 h-4 text-blue-600" />;
      case 'crm_account':
        return <ExternalLink className="w-4 h-4 text-green-600" />;
      case 'jira_org':
        return (
          <div className="w-4 h-4 rounded bg-blue-100 flex items-center justify-center">
            <JiraIcon className="w-3 h-3" />
          </div>
        );
      case 'zendesk_org':
        return (
          <div className="w-4 h-4 rounded bg-green-100 flex items-center justify-center">
            <span className="text-[10px] font-bold text-green-600">Z</span>
          </div>
        );
      case 'trello_org':
        return (
          <div className="w-4 h-4 rounded bg-blue-100 flex items-center justify-center">
            <TrelloIcon className="w-3 h-3" />
          </div>
        );
      case 'linear_org':
        return (
          <div className="w-4 h-4 rounded bg-indigo-100 flex items-center justify-center">
            <LinearIcon className="w-3 h-3" />
          </div>
        );
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSourceTypeLabel = (sourceType: string) => {
    switch (sourceType.toLowerCase()) {
      case 'slack_channel':
        return 'Slack Channel';
      case 'email_domain':
        return 'Email Domain';
      case 'crm_account':
        return 'CRM Account';
      default:
        return sourceType;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const extractMetadataInfo = (metadata: any) => {
    if (!metadata || typeof metadata !== 'object') return null;
    
    const info: any = {};
    
    // Extract interesting fields from metadata
    if (metadata.channel_name) info.channelName = metadata.channel_name;
    if (metadata.member_count !== undefined) info.memberCount = metadata.member_count;
    if (metadata.has_external_users !== undefined) info.hasExternalUsers = metadata.has_external_users;
    if (metadata.is_customer_channel !== undefined) info.isCustomerChannel = metadata.is_customer_channel;
    if (metadata.reasoning && Array.isArray(metadata.reasoning)) info.reasoning = metadata.reasoning;
    
    return Object.keys(info).length > 0 ? info : null;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border border-border bg-card/95 backdrop-blur-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6 border-b border-border">
          <div>
            <CardTitle className="text-2xl font-bold text-foreground">Data Sources</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Connected sources for {customerName}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="hover:bg-muted rounded-full">
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No data sources connected to this customer</p>
              <p className="text-sm text-muted-foreground mb-6">
                Connect sources through the Settings page to start tracking customer activity
              </p>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4">
                {sources.map((source, index) => {
                  const metadataInfo = extractMetadataInfo(source.metadata);
                  
                  return (
                    <div key={index} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          {getSourceIcon(source.source_type)}
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-semibold text-foreground">{source.source_name}</h4>
                              <Badge variant="outline" className="text-xs">
                                {getSourceTypeLabel(source.source_type)}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                              <div>
                                <span className="font-medium">Source ID:</span>
                                <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                                  {source.source_id}
                                </p>
                              </div>
                              
                              {source.confidence_score && (
                                <div>
                                  <span className="font-medium">Confidence Score:</span>
                                  <p className="mt-1">
                                    <Badge 
                                      variant={source.confidence_score >= 80 ? "default" : source.confidence_score >= 60 ? "secondary" : "destructive"}
                                      className="text-xs"
                                    >
                                      {source.confidence_score}%
                                    </Badge>
                                  </p>
                                </div>
                              )}
                              
                              {source.last_scanned && (
                                <div>
                                  <span className="font-medium">Last Scanned:</span>
                                  <p className="mt-1 flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    {formatDate(source.last_scanned)}
                                  </p>
                                </div>
                              )}
                              
                              {source.suggested_customer_name && (
                                <div>
                                  <span className="font-medium">Suggested Customer:</span>
                                  <p className="mt-1">{source.suggested_customer_name}</p>
                                </div>
                              )}
                            </div>
                            
                            {/* Improved metadata display */}
                            {metadataInfo && (
                              <div className="mt-3 space-y-2">
                                {metadataInfo.channelName && (
                                  <div className="flex items-center space-x-2">
                                    <MessageSquare className="w-3 h-3 text-gray-500" />
                                    <span className="text-sm text-muted-foreground">
                                      Channel: <span className="font-medium">{metadataInfo.channelName}</span>
                                    </span>
                                  </div>
                                )}
                                
                                {metadataInfo.memberCount !== undefined && (
                                  <div className="flex items-center space-x-2">
                                    <Users className="w-3 h-3 text-gray-500" />
                                    <span className="text-sm text-muted-foreground">
                                      Members: <span className="font-medium">{metadataInfo.memberCount}</span>
                                    </span>
                                  </div>
                                )}
                                
                                {metadataInfo.hasExternalUsers !== undefined && (
                                  <div className="flex items-center space-x-2">
                                    {metadataInfo.hasExternalUsers ? (
                                      <CheckCircle className="w-3 h-3 text-green-500" />
                                    ) : (
                                      <X className="w-3 h-3 text-gray-400" />
                                    )}
                                    <span className="text-sm text-muted-foreground">
                                      External Users: <span className="font-medium">
                                        {metadataInfo.hasExternalUsers ? 'Yes' : 'No'}
                                      </span>
                                    </span>
                                  </div>
                                )}
                                
                                {metadataInfo.isCustomerChannel !== undefined && (
                                  <div className="flex items-center space-x-2">
                                    {metadataInfo.isCustomerChannel ? (
                                      <CheckCircle className="w-3 h-3 text-blue-500" />
                                    ) : (
                                      <X className="w-3 h-3 text-gray-400" />
                                    )}
                                    <span className="text-sm text-muted-foreground">
                                      Customer Channel: <span className="font-medium">
                                        {metadataInfo.isCustomerChannel ? 'Yes' : 'No'}
                                      </span>
                                    </span>
                                  </div>
                                )}
                                
                                {metadataInfo.reasoning && metadataInfo.reasoning.length > 0 && (
                                  <div className="mt-2">
                                    <span className="text-sm font-medium text-muted-foreground">AI Reasoning:</span>
                                    <ul className="mt-1 space-y-1">
                                      {metadataInfo.reasoning.map((reason: string, idx: number) => (
                                        <li key={idx} className="text-xs text-muted-foreground flex items-start">
                                          <span className="text-gray-400 mr-1">•</span>
                                          {reason}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Remove button */}
                        <div className="ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveSource(source)}
                            disabled={removingSource === source.source_id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            {removingSource === source.source_id ? (
                              <div className="flex items-center space-x-1">
                                <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                                <span>Removing...</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1">
                                <X className="w-3 h-3" />
                                <span>Remove</span>
                              </div>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex justify-end pt-4 border-t border-border">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Edit Customer Modal Component
interface EditCustomerModalProps {
  customer: CustomerProfile;
  onClose: () => void;
  onSuccess: () => void;
}

const EditCustomerModal: React.FC<EditCustomerModalProps> = ({ customer, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: customer.name,
    domains: customer.domains || [],
    status: customer.status,
    mrr: customer.mrr,
    industry: customer.industry || '',
    company_size: customer.company_size || '',
    contract_value: customer.contract_value,
    contract_start: customer.contract_start ? new Date(customer.contract_start).toISOString().split('T')[0] : '',
    contract_end: customer.contract_end ? new Date(customer.contract_end).toISOString().split('T')[0] : '',
    crm_id: customer.crm_id || ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [domainInput, setDomainInput] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Customer name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiClient.updateCustomer(customer.customer_id, {
        name: formData.name.trim(),
        domains: formData.domains,
        status: formData.status,
        mrr: formData.mrr,
        industry: formData.industry || undefined,
        company_size: formData.company_size || undefined,
        contract_value: formData.contract_value,
        contract_start: formData.contract_start ? new Date(formData.contract_start) : undefined,
        contract_end: formData.contract_end ? new Date(formData.contract_end) : undefined,
        crm_id: formData.crm_id || undefined
      });

      onSuccess();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const addDomain = () => {
    if (domainInput.trim() && !formData.domains.includes(domainInput.trim())) {
      setFormData(prev => ({
        ...prev,
        domains: [...prev.domains, domainInput.trim()]
      }));
      setDomainInput('');
    }
  };

  const removeDomain = (domain: string) => {
    setFormData(prev => ({
      ...prev,
      domains: prev.domains.filter(d => d !== domain)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6 border-b border-gray-100 dark:border-gray-700">
          <div>
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Account</CardTitle>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Update account information</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                Basic Information
              </h4>
              
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Account Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Enter account name"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Domains</label>
                <div className="flex gap-2 mb-2 mt-1">
                  <input
                    type="text"
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    placeholder="example.com"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                  <Button type="button" onClick={addDomain} size="sm" className="bg-blue-600 hover:bg-blue-700">
                    Add
                  </Button>
                </div>
                {formData.domains.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.domains.map((domain) => (
                      <Badge key={domain} variant="secondary" className="cursor-pointer hover:bg-red-100 hover:text-red-800" onClick={() => removeDomain(domain)}>
                        {domain} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as CustomerStatus }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="prospect">🔍 Prospect</option>
                  <option value="active">✅ Active</option>
                  <option value="churned">❌ Churned</option>
                </select>
              </div>
            </div>

            {/* Financial Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                Financial Information
              </h4>
              
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Monthly Recurring Revenue</label>
                <input
                  type="number"
                  value={formData.mrr || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, mrr: e.target.value ? Number(e.target.value) : undefined }))}
                  placeholder="0"
                  min="0"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Contract Value</label>
                  <input
                    type="number"
                    value={formData.contract_value || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_value: e.target.value ? Number(e.target.value) : undefined }))}
                    placeholder="0"
                    min="0"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CRM ID</label>
                  <input
                    type="text"
                    value={formData.crm_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, crm_id: e.target.value }))}
                    placeholder="SF-001"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Contract Start</label>
                  <input
                    type="date"
                    value={formData.contract_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_start: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Contract End</label>
                  <input
                    type="date"
                    value={formData.contract_end}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_end: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                Company Information
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Industry</label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                    placeholder="Technology"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Company Size</label>
                  <input
                    type="text"
                    value={formData.company_size}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_size: e.target.value }))}
                    placeholder="1-50 employees"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="text-red-700 dark:text-red-400 text-sm flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                <span>{error}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose} 
                className="flex-1 py-3 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading} 
                className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span>Updating...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Edit className="h-4 w-4" />
                    <span>Update Customer</span>
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Delete Confirmation Modal Component
interface DeleteConfirmationModalProps {
  customerName: string;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ 
  customerName, 
  onClose, 
  onConfirm, 
  loading 
}) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-md">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold text-gray-900">Delete Customer</CardTitle>
              <p className="text-sm text-gray-600 mt-1">This action cannot be undone</p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete <strong>{customerName}</strong>? 
              This will permanently remove all customer data, including:
            </p>
            
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-4">
              <li>Customer profile and contact information</li>
              <li>Contract and financial data</li>
              <li>Associated data source mappings</li>
              <li>Historical activity and analytics</li>
            </ul>

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={onClose} 
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={onConfirm} 
                className="flex-1"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span>Deleting...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Trash2 className="h-4 w-4" />
                    <span>Delete Customer</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Briefing Modal Component
const BriefingModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  customer: CustomerProfile;
  onGenerateBriefing: (sinceDate: string) => Promise<void>;
  loading: boolean;
  briefingData: any;
  onClearBriefing: () => void;
}> = ({ isOpen, onClose, customer, onGenerateBriefing, loading, briefingData, onClearBriefing }) => {
  const [sinceDate, setSinceDate] = useState('');

  // Set default date to 7 days ago
  useEffect(() => {
    if (isOpen && !sinceDate) {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      setSinceDate(date.toISOString().split('T')[0]);
    }
  }, [isOpen, sinceDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sinceDate) {
      await onGenerateBriefing(sinceDate);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Pre-Call Briefing for {customer.name}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {!briefingData ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Analyze communications since:
              </label>
              <input
                type="date"
                value={sinceDate}
                onChange={(e) => setSinceDate(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                required
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !sinceDate}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Generate Briefing
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            {/* Briefing Header */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Briefing Summary
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Generated on {briefingData.generated_at ? new Date(briefingData.generated_at).toLocaleString() : 'Unknown Date'} • 
                    {briefingData.communications_count || 0} communications analyzed
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    onClearBriefing();
                    onClose();
                  }}
                >
                  Generate New Briefing
                </Button>
              </div>
            </div>

            {/* Overall Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Target className="w-5 h-5 mr-2" />
                  Overall Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {briefingData?.briefing?.overall_summary || 'No summary available'}
                </p>
              </CardContent>
            </Card>

            {/* Key Positives */}
            {briefingData?.briefing?.key_positives && briefingData.briefing.key_positives.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-green-700 dark:text-green-400">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Key Positives
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {briefingData?.briefing?.key_positives?.map((positive: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{positive}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Emerging Risks */}
            {briefingData?.briefing?.emerging_risks && briefingData.briefing.emerging_risks.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-orange-700 dark:text-orange-400">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Emerging Risks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {briefingData?.briefing?.emerging_risks?.map((risk: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <AlertCircle className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{risk}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Open Questions */}
            {briefingData?.briefing?.open_questions && briefingData.briefing.open_questions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-blue-700 dark:text-blue-400">
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Questions to Ask
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {briefingData?.briefing?.open_questions?.map((question: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <MessageSquare className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{question}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Notable Quotes */}
            {briefingData?.briefing?.notable_quotes && briefingData.briefing.notable_quotes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-purple-700 dark:text-purple-400">
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Notable Quotes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {briefingData?.briefing?.notable_quotes?.map((quote: any, index: number) => (
                      <div key={index} className="border-l-4 border-purple-500 pl-4 py-2">
                        <blockquote className="text-gray-700 dark:text-gray-300 italic mb-2">
                          "{quote.quote}"
                        </blockquote>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          — {quote.speaker_name || 'Unknown Speaker'}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  onClearBriefing();
                  onClose();
                }}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetailPage;